// Phase B Block 3 — «адекватность самооценки сервиса» block.
//
// Compact KPI rows (overestimate / conservative share, median dR/σ,
// the σ↔dr correlation) plus a collapsible top-5 «blind spots» list —
// stations where the service's σ badly under-states the real error.
// Computed over the strict-clean cohort with a non-sentinel σ;
// `sentinel_excluded` is surfaced so the operator trusts the cohort.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { SelfAssessmentResponse } from '../../api/types';
import { formatConfig } from './formatConfig';

/** Verbal strength label for a Spearman ρ so the operator doesn't
 *  have to interpret the bare coefficient. Uses |ρ| — the sign is
 *  shown separately as the numeric value. */
function spearmanStrength(rho: number): string {
  const a = Math.abs(rho);
  if (a < 0.2) return 'очень слабая';
  if (a < 0.4) return 'слабая';
  if (a < 0.6) return 'умеренная';
  if (a < 0.8) return 'заметная';
  return 'сильная';
}

export function SelfAssessmentBlock({
  data,
}: {
  data: SelfAssessmentResponse;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (data.cohort === 0) {
    return (
      <div className="selfassess-section">
        <div className="section-title">СКО и расхождение с эталоном</div>
        <div className="analytics-empty">
          нет задач с эталоном и валидной σ
          {data.sentinel_excluded > 0
            ? ` (${data.sentinel_excluded} с σ-заглушкой исключены)`
            : ''}
        </div>
      </div>
    );
  }

  // Phase G.3 — verdict теперь нейтрально-методологический: σ —
  // самооценка решения МНК, не «отметка» обработки. Расхождение
  // с эталоном может идти от датума сети, тропосферы или привязки
  // пункта, поэтому даже при сильной корреляции σ остаётся одним
  // из источников оценки, а не финальным вердиктом.
  const verdict =
    data.spearman != null && Math.abs(data.spearman) >= 0.6
      ? 'σ согласуется с реальным расхождением (можно опираться)'
      : 'σ не отражает систематические сдвиги (датум сети, тропосфера, привязка пункта)';

  return (
    <div className="selfassess-section">
      <div className="section-title">СКО и расхождение с эталоном</div>
      <div className="selfassess-card" data-testid="selfassess-card">
        <div className="selfassess-caption">
          СКО vs реальная ошибка, по {data.cohort} задачам с эталоном
          {data.sentinel_excluded > 0 && (
            <> · {data.sentinel_excluded} с σ-заглушкой исключены</>
          )}
        </div>
        <div className="selfassess-row">
          <span className="selfassess-key">σ меньше |dR|</span>
          <span
            className="selfassess-val"
            data-testid="selfassess-overestimate"
          >
            {data.overestimate_pct}% задач
          </span>
        </div>
        <div className="selfassess-row">
          <span className="selfassess-key">σ больше |dR|</span>
          <span className="selfassess-val">
            {data.conservative_pct}% задач
          </span>
        </div>
        <div className="selfassess-row">
          <span className="selfassess-key">медиана dR / σ</span>
          <span className="selfassess-val">
            {data.median_ratio != null ? data.median_ratio.toFixed(2) : '—'}
          </span>
        </div>
        <div className="selfassess-row">
          <span className="selfassess-key">связь СКО ↔ реальная ошибка</span>
          <span className="selfassess-val">
            {data.spearman != null
              ? `${spearmanStrength(data.spearman)} (ρ=${data.spearman.toFixed(2)})`
              : '—'}
          </span>
        </div>
        <div className="selfassess-verdict">вывод: {verdict}</div>
      </div>

      {data.blind_spots.length > 0 && (
        <div className="selfassess-blind">
          <button
            type="button"
            className="selfassess-blind-toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            data-testid="selfassess-blind-toggle"
          >
            <span className="selfassess-blind-caret" aria-hidden="true">
              {expanded ? '▴' : '▾'}
            </span>{' '}
            СТАНЦИИ С НАИБОЛЬШИМ РАСХОЖДЕНИЕМ σ И |dR| ({data.blind_spots.length})
          </button>
          {expanded && (
            <div className="proctime-wrap">
              <table
                className="selfassess-blind-table"
                data-testid="selfassess-blind-table"
              >
                <thead>
                  <tr>
                    <th className="proctime-th-config">станция</th>
                    <th>конфиг</th>
                    <th>|dR|</th>
                    <th>σ</th>
                    <th>dR/σ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.blind_spots.map((b) => (
                    <tr
                      key={b.job_id}
                      className="selfassess-blind-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/job/${b.job_id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/job/${b.job_id}`);
                        }
                      }}
                      title={`Открыть карточку задачи #${b.job_id}`}
                      data-testid={`selfassess-blind-row-${b.job_id}`}
                    >
                      <td className="selfassess-blind-station">{b.station}</td>
                      <td>{formatConfig(b.config)}</td>
                      <td className="proctime-num">{b.dr_3d_mm.toFixed(1)}</td>
                      <td className="proctime-num">
                        {b.sigma_3d_mm.toFixed(1)}
                      </td>
                      <td className="proctime-num selfassess-blind-ratio">
                        ×{b.ratio.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
